import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { QueryParams, SortOrder, TabType } from './types';

const DEFAULT_PER_PAGE = 25;
const DEFAULT_PAGE = 1;
const DEFAULT_TAB: TabType = 'unbilled';

// Default sort fields for each tab that match backend defaults
const DEFAULT_SORT_BY_TAB: Record<TabType, string> = {
  unbilled: 'scheduled_for',
  claims: 'dateOfService',
  remittances: 'created_at',
};
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<QueryParams>(() => {
    const tab = (searchParams.get('tab') as TabType) || DEFAULT_TAB;
    return {
      tab,
      patient: searchParams.get('patient') || undefined,
      from: searchParams.get('from') || undefined,
      to: searchParams.get('to') || undefined,
      status: searchParams.get('status') || undefined,
      providers: searchParams.get('providers') || undefined,
      hasClaim: searchParams.get('hasClaim') || undefined,
      billable: searchParams.get('billable') || undefined,
      sort: searchParams.get('sort') || DEFAULT_SORT_BY_TAB[tab],
      order: (searchParams.get('order') as SortOrder) || DEFAULT_SORT_ORDER,
      page: Number(searchParams.get('page')) || DEFAULT_PAGE,
      perPage: Number(searchParams.get('perPage')) || DEFAULT_PER_PAGE,
    };
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Partial<QueryParams>, resetPage = false) => {
      const newParams = new URLSearchParams(searchParams);

      // Apply updates
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      // Reset page to 1 when filters or sorting changes (but not when page itself changes)
      if (resetPage && !('page' in updates)) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    newParams.set('tab', params.tab);
    newParams.set('page', '1');
    newParams.set('perPage', String(params.perPage));
    // Preserve default sort settings
    newParams.set('sort', DEFAULT_SORT_BY_TAB[params.tab]);
    newParams.set('order', DEFAULT_SORT_ORDER);
    setSearchParams(newParams);
  }, [params.tab, params.perPage, setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      params.patient ||
      params.from ||
      params.to ||
      params.status ||
      params.providers ||
      params.hasClaim ||
      params.billable,
    );
  }, [params]);

  return {
    params,
    updateParams,
    clearFilters,
    hasActiveFilters,
  };
};
